import React, { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  ChevronRight,
  ChevronLeft,
  CheckCircle,
  ArrowRight,
  Mail,
} from "lucide-react";
import { SUPPORT_EMAIL } from "../../utils/supportContacts";
import "./clients.css";

const STEPS = [
  { id: "intro",    label: "Introduction" },
  { id: "problem",  label: "The Problem" },
  { id: "solution", label: "What VisuN AI Does" },
  { id: "pricing",  label: "Pricing" },
  { id: "privacy",  label: "Data & Privacy" },
  { id: "terms",    label: "Terms" },
  { id: "start",    label: "Next Steps" },
];

export default function ClientsPage() {
  const [active, setActive] = useState(0);
  const [seen, setSeen] = useState(new Set());
  const [menuOpen, setMenuOpen] = useState(false);
  const mainRef = useRef(null);
  const navigate = useNavigate();

  function go(i) {
    setSeen(prev => { const s = new Set(prev); s.add(i); return s; });
    setActive(i);
    setMenuOpen(false);
    if (mainRef.current) mainRef.current.scrollTop = 0;
  }

  function next() { if (active < STEPS.length - 1) go(active + 1); }
  function prev() { if (active > 0) go(active - 1); }

  const pct = Math.round(((active + 1) / STEPS.length) * 100);

  const PAGES = [
    <PageIntro    key="intro" />,
    <PageProblem  key="problem" />,
    <PageSolution key="solution" />,
    <PagePricing  key="pricing" />,
    <PagePrivacy  key="privacy" />,
    <PageTerms    key="terms" />,
    <PageStart    key="start" navigate={navigate} />,
  ];

  return (
    <div className="cp-root">
      <header className="cp-bar">
        <div className="cp-bar-inner">
          <span className="cp-logo" onClick={() => navigate("/")}>
            VisuN<span className="cp-logo-ai">AI</span>
          </span>
          <div className="cp-prog-track">
            <div className="cp-prog-fill" style={{ width: `${pct}%` }} />
          </div>
          <span className="cp-pct">{active + 1} / {STEPS.length}</span>
          <button className="cp-hamburger" onClick={() => setMenuOpen(!menuOpen)}>
            <span /><span /><span />
          </button>
        </div>
      </header>

      <div className="cp-body">
        {/* ── SIDEBAR ── */}
        <nav className={`cp-nav ${menuOpen ? "cp-nav--open" : ""}`}>
          <p className="cp-nav-label">Contents</p>
          {STEPS.map((s, i) => (
            <button
              key={s.id}
              className={`cp-nav-item ${active === i ? "is-active" : ""} ${seen.has(i) && active !== i ? "is-seen" : ""}`}
              onClick={() => go(i)}
            >
              <span className="cp-nav-dot">
                {seen.has(i) && active !== i ? <CheckCircle size={11} /> : <span>{i + 1}</span>}
              </span>
              {s.label}
            </button>
          ))}
          <a className="cp-nav-mail" href={`mailto:${SUPPORT_EMAIL}`}>
            <Mail size={12} /> {SUPPORT_EMAIL}
          </a>
        </nav>

        {menuOpen && <div className="cp-veil" onClick={() => setMenuOpen(false)} />}

        {/* ── PAGE ── */}
        <main className="cp-main" ref={mainRef}>
          <div className="cp-page">
            {PAGES[active]}

            <div className="cp-foot">
              {active > 0 && (
                <button className="cp-ghost" onClick={prev}>
                  <ChevronLeft size={15} /> Back
                </button>
              )}
              <span />
              {active < STEPS.length - 1 && (
                <button className="cp-solid" onClick={next}>
                  {active === STEPS.length - 2 ? "Finish" : "Continue"}
                  <ChevronRight size={15} />
                </button>
              )}
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}

/* ════════════════════════════════════════════════════════
   PAGE 1 — INTRODUCTION
════════════════════════════════════════════════════════ */
function PageIntro() {
  return (
    <article className="cp-article">
      <p className="cp-eyebrow">Pre-Sales Document · Confidential</p>

      <h1 className="cp-h1">
        See everything<br />happening inside<br />your organisation.
      </h1>

      <p className="cp-lead">
        VisuN AI is an employee monitoring and device management platform
        built for Indian enterprises. This document covers what we do,
        what it costs, how your data is protected, and what happens next.
      </p>

      <div className="cp-rule" />

      <div className="cp-meta-row">
        <div className="cp-meta-item">
          <span className="cp-meta-label">Prepared by</span>
          <span className="cp-meta-value">VisuN AI · Global Cyber Associates</span>
        </div>
        <div className="cp-meta-item">
          <span className="cp-meta-label">Contact</span>
          <span className="cp-meta-value">{SUPPORT_EMAIL}</span>
        </div>
        <div className="cp-meta-item">
          <span className="cp-meta-label">Date</span>
          <span className="cp-meta-value">{new Date().toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" })}</span>
        </div>
      </div>

      <div className="cp-rule" />

      <p className="cp-body-text">
        This document is structured to walk you through VisuN AI step by step —
        from the problem it solves, to the exact features you get, the cost,
        and how your employees' data is handled. Use the sidebar to jump to
        any section, or follow the flow using the Continue button below.
      </p>

      <div className="cp-callout">
        The entire walkthrough takes under 5 minutes to read. We recommend
        reading it in order before your first demo call.
      </div>
    </article>
  );
}

/* ════════════════════════════════════════════════════════
   PAGE 2 — THE PROBLEM
════════════════════════════════════════════════════════ */
function PageProblem() {
  return (
    <article className="cp-article">
      <p className="cp-eyebrow">The Problem</p>

      <h1 className="cp-h1">
        You're paying for<br />time you cannot see.
      </h1>

      <p className="cp-lead">
        Most organisations have no real visibility into what their
        employees are actually doing at work — on company devices,
        on company time.
      </p>

      <div className="cp-rule" />

      {[
        {
          n: "01",
          head: "No attendance clarity",
          body: "Manual check-ins are gamed. Biometric systems don't track actual work hours — only entry and exit. There's no record of when an employee was actually productive.",
        },
        {
          n: "02",
          head: "No device visibility",
          body: "IT teams don't know what software is installed, which devices are healthy, or when machines go offline mid-day. Issues are found only after they cause downtime.",
        },
        {
          n: "03",
          head: "No accountability on remote or hybrid teams",
          body: "When employees work from home or branch offices, managers rely entirely on self-reporting. There's no objective data on hours worked or applications used.",
        },
        {
          n: "04",
          head: "No compliance trail",
          body: "If something goes wrong — a data leak, an unauthorised install, a dispute over hours — there's nothing to look back at. No audit trail, no evidence, no protection.",
        },
      ].map(({ n, head, body }) => (
        <div key={n} className="cp-problem-item">
          <span className="cp-problem-num">{n}</span>
          <div>
            <h3 className="cp-problem-head">{head}</h3>
            <p className="cp-body-text">{body}</p>
          </div>
        </div>
      ))}

      <div className="cp-callout cp-callout--warn">
        These are not edge cases. They are the daily reality for most
        organisations with 10 or more employees.
      </div>
    </article>
  );
}

/* ════════════════════════════════════════════════════════
   PAGE 3 — WHAT VISUN DOES
════════════════════════════════════════════════════════ */
function PageSolution() {
  return (
    <article className="cp-article">
      <p className="cp-eyebrow">What VisuN AI Does</p>

      <h1 className="cp-h1">
        One platform.<br />Complete visibility.
      </h1>

      <p className="cp-lead">
        A lightweight agent installs on each Windows device. From that moment,
        your admin dashboard shows you everything — in real time.
      </p>

      <div className="cp-rule" />

      <h2 className="cp-h2">What you can see</h2>

      {[
        {
          title: "Every device, live",
          desc: "Online / offline status, CPU load, memory usage, disk health, and OS version — for every machine in your organisation, on a single screen.",
        },
        {
          title: "Actual work hours",
          desc: "Attendance is tracked automatically by system activity. No manual check-ins. Reports show exact hours worked per employee, per day, per department.",
        },
        {
          title: "What applications are open",
          desc: "See which applications are running and for how long. Know whether your team is using the tools you're paying for, or something else.",
        },
        {
          title: "Websites visited",
          desc: "A full log of URLs accessed during work hours. Filter by employee, department, or time range.",
        },
        {
          title: "Live screen — on demand",
          desc: "View any employee's screen in real time. Sessions require admin action, are logged, and auto-expire after 30 minutes.",
        },
        {
          title: "Files and USB activity",
          desc: "Track file access events and USB device connections. Restrict USB ports remotely if needed.",
        },
      ].map(({ title, desc }) => (
        <div key={title} className="cp-sol-item">
          <CheckCircle size={16} className="cp-sol-check" />
          <div>
            <strong>{title}</strong>
            <p className="cp-body-text">{desc}</p>
          </div>
        </div>
      ))}

      <div className="cp-rule" />

      <h2 className="cp-h2">What your management team gets</h2>
      <p className="cp-body-text">
        Beyond the live feed, VisuN AI gives HR, IT, and Finance their own dashboards.
        Attendance summaries. Productivity scores. Software cost analysis.
        All exportable, all up to date.
      </p>

      <div className="cp-callout">
        VisuN AI does not require any IT infrastructure changes. No VPN, no
        on-premise server. Agents connect directly to the cloud. Deployment
        takes under 10 minutes for the first device.
      </div>
    </article>
  );
}

/* ════════════════════════════════════════════════════════
   PAGE 4 — PRICING
════════════════════════════════════════════════════════ */
function PagePricing() {
  return (
    <article className="cp-article">
      <p className="cp-eyebrow">Pricing</p>

      <h1 className="cp-h1">
        Simple pricing.<br />No surprises.
      </h1>

      <p className="cp-lead">
        VisuN AI is billed per seat — one seat equals one monitored device.
        Pick the plan that matches your needs. Switch or scale any time.
      </p>

      <div className="cp-rule" />

      {[
        {
          tier: "Free Trial",
          seats: "Up to 3 devices",
          price: "₹0",
          note: "No credit card. One trial per workspace. Trial data is retained for 7 days after expiry.",
          includes: [
            "Device monitoring dashboard",
            "Basic attendance tracking",
            "Admin account setup",
          ],
          highlight: false,
        },
        {
          tier: "Essentials",
          seats: "Per seat · Monthly or Annual",
          price: "Contact us",
          note: "Best for small teams getting started with monitoring.",
          includes: [
            "Everything in Free Trial",
            "URL and application tracking",
            "Live screen viewer",
            "Department management",
          ],
          highlight: false,
        },
        {
          tier: "Professional",
          seats: "Per seat · Monthly or Annual",
          price: "Contact us",
          note: "Most organisations choose Professional. Covers the full feature set.",
          includes: [
            "Everything in Essentials",
            "File monitor and USB control",
            "Analytics and CFO dashboards",
            "VisuN AI features",
            "Priority support",
          ],
          highlight: true,
        },
        {
          tier: "Enterprise",
          seats: "Custom seat count · Annual",
          price: "Custom quote",
          note: "For large deployments with dedicated support and custom SLA.",
          includes: [
            "Everything in Professional",
            "Custom onboarding",
            "Dedicated support contact",
            "SLA guarantee",
          ],
          highlight: false,
        },
      ].map(({ tier, seats, price, note, includes, highlight }) => (
        <div key={tier} className={`cp-plan ${highlight ? "cp-plan--on" : ""}`}>
          {highlight && <span className="cp-plan-tag">Recommended</span>}
          <div className="cp-plan-top">
            <div>
              <h3 className="cp-plan-name">{tier}</h3>
              <p className="cp-plan-seats">{seats}</p>
            </div>
            <span className="cp-plan-price">{price}</span>
          </div>
          <p className="cp-plan-note">{note}</p>
          <ul className="cp-plan-list">
            {includes.map(f => (
              <li key={f}><CheckCircle size={12} />{f}</li>
            ))}
          </ul>
        </div>
      ))}

      <div className="cp-rule" />

      <p className="cp-body-text">
        All prices are in INR and exclude 18% GST. Annual plans are discounted.
        To get exact per-seat pricing for your headcount, email{" "}
        <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>{" "}
        with the number of devices you need to monitor.
      </p>
    </article>
  );
}

/* ════════════════════════════════════════════════════════
   PAGE 5 — DATA & PRIVACY
════════════════════════════════════════════════════════ */
function PagePrivacy() {
  return (
    <article className="cp-article">
      <p className="cp-eyebrow">Data & Privacy</p>

      <h1 className="cp-h1">
        Your data belongs<br />to you. Full stop.
      </h1>

      <p className="cp-lead">
        VisuN AI handles sensitive data — employee activity, device logs,
        screen sessions. Here is exactly how it is stored, protected,
        and never misused.
      </p>

      <div className="cp-rule" />

      {[
        {
          q: "Who can see your data?",
          a: "Only your organisation's admins. Each workspace is fully isolated — no VisuN AI team member can access your monitoring data except during a support session you explicitly authorise.",
        },
        {
          q: "Is the data encrypted?",
          a: "Yes. All communication between agents and the cloud is encrypted via TLS/HTTPS. Screen sessions use encrypted WebRTC peer connections. Data at rest is stored in an encrypted database.",
        },
        {
          q: "Do you sell or share monitoring data?",
          a: "No. VisuN AI does not sell, share, or use your monitoring data for any purpose other than running the platform on your behalf. We are not in the data business.",
        },
        {
          q: "How long is data kept?",
          a: "For the duration of your active subscription. Trial data is purged 7 days after expiry. You can request full data deletion at any time by writing to us.",
        },
        {
          q: "What about employee consent?",
          a: "Indian law requires that employees be informed before monitoring begins. We provide a ready-made Employee Monitoring Notice for your HR team to distribute before agent deployment. The responsibility to do so lies with your organisation.",
        },
        {
          q: "What if there is a breach?",
          a: "We have a documented incident response process. Affected customers are notified within 72 hours. We carry cyber liability insurance.",
        },
      ].map(({ q, a }) => (
        <div key={q} className="cp-faq-item">
          <h4 className="cp-faq-q">{q}</h4>
          <p className="cp-body-text">{a}</p>
        </div>
      ))}

      <div className="cp-callout">
        Need a Data Processing Agreement (DPA) or a security questionnaire
        filled? Email us at{" "}
        <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>.
        We respond within one business day.
      </div>
    </article>
  );
}

/* ════════════════════════════════════════════════════════
   PAGE 6 — TERMS
════════════════════════════════════════════════════════ */
const TERMS = [
  {
    t: "Subscription & Access",
    b: "By activating any paid plan, you agree to these Terms. Access to features is plan-specific and subject to change with prior notice.",
  },
  {
    t: "Payment",
    b: "Prices are in INR, exclusive of 18% GST. Payments are processed via Razorpay. By completing a purchase, you authorise the charge to your selected method.",
  },
  {
    t: "Refunds",
    b: `Fees are non-refundable unless a technical failure on our side prevented subscription activation. Refund requests must be raised within 48 hours at ${SUPPORT_EMAIL}.`,
  },
  {
    t: "Free Trial",
    b: "Limited to 3 systems. Available once per workspace. Trial data is retained for 7 days after expiry. No payment details required.",
  },
  {
    t: "Acceptable Use",
    b: "You may only use VisuN AI to monitor devices and employees within your own organisation, as permitted by applicable law. Resale, reverse-engineering, or monitoring persons outside your org will result in immediate suspension.",
  },
  {
    t: "Data & Privacy",
    b: "You are responsible for obtaining employee consent before deployment. VisuN AI processes monitoring data only to operate the platform on your behalf.",
  },
  {
    t: "Uptime",
    b: "We target 99.5% monthly uptime. Scheduled maintenance is communicated 24 hours in advance. We are not liable for third-party outages.",
  },
  {
    t: "Liability",
    b: "Our aggregate liability for any claim shall not exceed the total amount you paid in the three months preceding the claim.",
  },
  {
    t: "Changes to Terms",
    b: "We may update these Terms. Material changes are communicated by email or in-app notice 14 days before taking effect.",
  },
  {
    t: "Governing Law",
    b: "These Terms are governed by Indian law. Disputes are subject to the exclusive jurisdiction of courts in Hyderabad, Telangana.",
  },
];

function PageTerms() {
  const [open, setOpen] = useState(null);
  return (
    <article className="cp-article">
      <p className="cp-eyebrow">Terms & Conditions</p>

      <h1 className="cp-h1">
        Plain language.<br />No fine print.
      </h1>

      <p className="cp-lead">
        These are the terms that govern your use of VisuN AI. Each clause is
        written to be understood, not buried. Click any clause to expand it.
      </p>

      <div className="cp-rule" />

      <div className="cp-terms-stack">
        {TERMS.map((item, i) => (
          <div key={i} className={`cp-clause ${open === i ? "cp-clause--open" : ""}`}>
            <button className="cp-clause-head" onClick={() => setOpen(open === i ? null : i)}>
              <span className="cp-clause-n">{String(i + 1).padStart(2, "0")}</span>
              <span className="cp-clause-title">{item.t}</span>
              <ChevronRight size={14} className="cp-clause-arrow" />
            </button>
            {open === i && <p className="cp-clause-body">{item.b}</p>}
          </div>
        ))}
      </div>

      <p className="cp-terms-note">
        By activating a subscription, you accept these terms in full.{" "}
        <a href="/terms" target="_blank" rel="noopener noreferrer">
          View full terms →
        </a>
      </p>
    </article>
  );
}

/* ════════════════════════════════════════════════════════
   PAGE 7 — NEXT STEPS
════════════════════════════════════════════════════════ */
function PageStart({ navigate }) {
  return (
    <article className="cp-article">
      <p className="cp-eyebrow">Next Steps</p>

      <h1 className="cp-h1">
        You've seen everything.<br />Here's how to begin.
      </h1>

      <p className="cp-lead">
        Three clear options — pick whichever fits where you are right now.
      </p>

      <div className="cp-rule" />

      <div className="cp-steps-stack">
        <div className="cp-next-step">
          <div className="cp-next-num">A</div>
          <div className="cp-next-body">
            <h3>Start the free trial</h3>
            <p>
              Create your workspace and monitor up to 3 devices at no cost.
              No credit card required. You'll be live in under 10 minutes.
            </p>
            <button className="cp-solid" onClick={() => navigate("/register")}>
              Create Account <ArrowRight size={14} />
            </button>
          </div>
        </div>

        <div className="cp-next-step">
          <div className="cp-next-num">B</div>
          <div className="cp-next-body">
            <h3>Book a demo call</h3>
            <p>
              Not ready to self-start? Email us to schedule a 30-minute
              walkthrough. We'll show you the platform live and answer
              every question before you commit to anything.
            </p>
            <a className="cp-ghost" href={`mailto:${SUPPORT_EMAIL}`}>
              Email to Book Demo <Mail size={14} />
            </a>
          </div>
        </div>

        <div className="cp-next-step">
          <div className="cp-next-num">C</div>
          <div className="cp-next-body">
            <h3>Get a custom quote</h3>
            <p>
              Have a specific headcount, deployment timeline, or compliance
              requirement? Send us your details and we'll prepare a
              tailored proposal within 24 hours.
            </p>
            <a className="cp-ghost" href={`mailto:${SUPPORT_EMAIL}?subject=Custom Quote Request`}>
              Request a Quote <ArrowRight size={14} />
            </a>
          </div>
        </div>
      </div>

      <div className="cp-rule" />

      <div className="cp-done-box">
        <CheckCircle size={18} />
        <div>
          <strong>You've read the full document.</strong>
          <p>
            Share this link with your team or leadership before the next conversation:{" "}
            <span className="cp-done-link">visunai.com/clients</span>
          </p>
        </div>
      </div>
    </article>
  );
}
